/**
 * @format
 */
import axios from "axios";
import { uri } from "constants/common";
import i18n from "../i18n/i18n";

const getAppLanguageHeader = () => {
  const localLanguage = localStorage.getItem("app_language");
  if (localLanguage && ["en", "es"].includes(localLanguage)) {
    return localLanguage;
  }

  if (i18n?.language && ["en", "es"].includes(i18n.language)) {
    return i18n.language;
  }

  return "en";
};

export default class Api {
  _api = null;
  static _token;

  constructor() {
    this._api = axios.create({
      baseURL: uri, // replace with your actual base URL
    });

    // Request Interceptor (For Adding Token in Headers)
    this._api.interceptors.request.use(
      (config) => {
        if (Api._token) {
          config.headers.Authorization = `Bearer ${Api._token}`;
        }
        config.headers["Accept-Language"] = getAppLanguageHeader();
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response Interceptor (For Handling 401 Unauthorized)
    this._api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && error.config.url !== "/login") {
          console.error("Unauthorized! Logging out...");
          localStorage.clear();
          setTimeout(() => {
            window.location.href = "/login";
          }, 500);
          return Promise.reject({ message: "Unauthorized access" });
        }
        return Promise.reject(error);
      }
    );
  }

  static init = ({ url }) => {
    try {
      this._api = axios.create({
        baseURL: url,
        timeout: 10000,
      });

      // Add response interceptor after reinitialization
      this._api.interceptors.request.use(
        (config) => {
          if (Api._token) {
            config.headers.Authorization = `Bearer ${Api._token}`;
          }
          config.headers["Accept-Language"] = getAppLanguageHeader();
          return config;
        },
        (error) => Promise.reject(error)
      );

      this._api.interceptors.response.use(
        (response) => response,
        (error) => {
          if (error.response?.status === 401 && error.config.url !== "/login") {
            localStorage.clear();
            setTimeout(() => {
              window.location.href = "/login";
            }, 500);
            return Promise.reject({ message: "Unauthorized access" });
          }
          return Promise.reject(error);
        }
      );
    } catch (error) {
      return error;
    }
  };

  static removeClientToken = async () => {
    this.requestInterceptor = undefined;
    this._api.interceptors.request.clear();
  };

  static setClientToken = async (token) => {
    Api._token = token;
    // this.token = token;
    // this.interceptor = this._api.interceptors.request.use(function (config) {
    //   config.headers.Authorization = `Bearer ${token}`;
    //   this.requestInterceptor = token;
    //   return config;
    // })
  };

  static getTokenFromInterceptor = () => {
    return this.requestInterceptor;
  };

  //#region AUTH APIS
  static login = async (data) => {
    try {
      const response = await this._api.post("/login", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static signup = async (data) => {
    try {
      const response = await this._api.post("/signup", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static forgotPassword = async (data) => {
    try {
      const response = await this._api.post("/forgot-password", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static resetPassword = async (data) => {
    try {
      const response = await this._api.post("/reset-password", data);
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  static addLanguageRequest = async (data) => {
    try {
      const response = await this._api.post("account/language-requests", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateLanguageRequest = async (id, data) => {
    try {
      const response = await this._api.post("account/language-requests/edit/" + id, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchLanguageRequests = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("account/language-requests/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };


  static fetchAllLanguageRequests = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("client/language-requests/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static approveLanguageRequest = async (id) => {
    try {
      const response = await this._api.post(
        "client/language-requests/move-to-client-request/" + id,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static moveToLanguageRequest = async (id) => {
    try {
      const response = await this._api.post(
        "/client/language-requests/move-to-language-request/" + id,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static rejectLanguageRequest = async (id, data) => {
    try {
      const response = await this._api.post(
        "client/language-requests/cancel/" + id,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region PROFILE

  static changePassword = async (data) => {
    try {
      const response = await this._api.post("/changepassword", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region HOSPITAL
  static addHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateHospital = async (data, itemId) => {
    try {
      const response = await this._api.post("/hospital/edit/" + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeHospital = async (itemId) => {
    try {
      const response = await this._api.post("/hospital/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchHospitals = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/hospital/list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region POOL
  static addPool = async (data) => {
    try {
      const response = await this._api.post("/pool", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updatePool = async (data, itemId) => {
    try {
      const response = await this._api.post("/pool/edit/" + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removePool = async (itemId) => {
    try {
      const response = await this._api.post("/pool/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePools = async (data) => {
    try {
      const response = await this._api.post("/pool/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchPools = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/pool/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region  MEETING PLACE
  static addMeetingPlace = async (data) => {
    try {
      const response = await this._api.post("/meetingplace", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateMeetingPlace = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/meetingplace/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMeetingPlace = async (itemId) => {
    try {
      const response = await this._api.post("/meetingplace/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePlaces = async (data) => {
    try {
      const response = await this._api.post("/meetingplace/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMeetingPlaces = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/meetingplace/list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region Country 
  
    static fetchCountries = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/country/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region Timezones 
  
    static fetchTimezone = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/country/timezone", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  
  //#endregion

  //#region CITY
  static addCity = async (data) => {
    try {
      const response = await this._api.post("/client/city", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateCity = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/city/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeCity = async (itemId) => {
    try {
      const response = await this._api.post("/client/city/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleCitys = async (data) => {
    try {
      const response = await this._api.post("/client/city/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchCitys = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/city/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region STATE
  static addState = async (data) => {
    try {
      const response = await this._api.post("/client/state", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateState = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/state/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeState = async (itemId) => {
    try {
      const response = await this._api.post("/client/state/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleStates = async (data) => {
    try {
      const response = await this._api.post("/client/state/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchStates = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/state/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region LANGUAGES
  static addLanguage = async (data) => {
    try {
      const response = await this._api.post("/client/language", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateLanguage = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/language/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeLanguage = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/language/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleLanguages = async (data) => {
    try {
      const response = await this._api.post("/client/language/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchLanguages = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/language/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchPreferredLanguages = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/language/preferred-list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchTiersLanguages = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/language/tier-languages", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region LANGUAGES TIERS
  static addLanguageTiers = async (data) => {
    try {
      const response = await this._api.post("/client/language-tiers", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateLanguageTiers = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/language-tiers/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeLanguageTiers = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/language-tiers/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleLanguagesTiers = async (data) => {
    try {
      const response = await this._api.post(
        "/client/language-tiers/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchLanguagesTiers = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/language-tiers/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTemplateLanguagesTiers = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/language-tiers/template-tiers",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region VENDOR
  static addVendor = async (formData) => {
    try {
      const response = await this._api.post("/vendor", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateVendor = async (formData, itemId) => {
    try {
      const response = await this._api.post(
        "/vendor/edit/" + itemId,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeVendor = async (itemId) => {
    try {
      const response = await this._api.post("/vendor/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleVendors = async (data) => {
    try {
      const response = await this._api.post("/vendor/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchVendors = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/vendor/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region ADMIN
  static addAdmin = async (formData) => {
    try {
      const response = await this._api.post("/admin", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateAdmin = async (formData, itemId) => {
    try {
      const response = await this._api.post("/admin/edit/" + itemId, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeAdmin = async (itemId) => {
    try {
      const response = await this._api.post("/admin/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleAdmins = async (data) => {
    try {
      const response = await this._api.post("/admin/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAdmins = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/admin/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region TOP HOSPITAL
  static addTopHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateTopHospital = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/hospital-list/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeTopHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list/remove/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleTopHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTopHospitals = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/hospital-list/top-hospital", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region OTHER HOSPITAL
  static addOtherHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateOtherHospital = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/hospital-list/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeOtherHospital = async (itemId) => {
    try {
      const response = await this._api.post("/hospital-list/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleOtherHospital = async (data) => {
    try {
      const response = await this._api.post("/hospital-list/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchOtherHospitals = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/hospital-list/other-hospital", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region AVAILIBILITY
  static addAvailibility = async (data) => {
    try {
      const response = await this._api.post("/availability", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateAvailibility = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/availability/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeAvailibility = async (itemId) => {
    try {
      const response = await this._api.post("/availability/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleAvailibilitys = async (data) => {
    try {
      const response = await this._api.post("/availability/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAvailibilitys = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/availability/list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region SCHEDULE
  static fetchAvailableInterpreters = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/schedule/available-interpreters", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static addLunchSchedule = async (data) => {
    try {
      const response = await this._api.post("/schedule/lunch-store", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static addSchedule = async (data) => {
    try {
      const response = await this._api.post("/schedule", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateSchedule = async (data, itemId) => {
    try {
      const response = await this._api.post("/schedule/edit/" + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeSchedule = async (itemId) => {
    try {
      const response = await this._api.post("/schedule/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleSchedules = async (data) => {
    try {
      const response = await this._api.post("/schedule/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchSchedules = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/schedule-list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchScheduleSummaryList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/schedule/summary", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchScheduleView = async (id, query) => {
    // let params = { page: page };
    // if (query && Object.keys(query).length > 0) {
    //   Object.keys(query).map((value) => {
    //     if (query[value]) {
    //       params[value] = query[value];
    //     }
    //   });
    // };

    try {
      const response = await this._api.get(`/schedule/view/${id}`, {
        // params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region PATIENT
  static addPatient = async (data) => {
    try {
      const response = await this._api.post("/patient", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static filterPatient = async (data) => {
    try {
      const response = await this._api.post("/patient/filter-patient", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updatePatient = async (data, itemId) => {
    try {
      const response = await this._api.post("/patient/edit/" + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removePatient = async (itemId) => {
    try {
      const response = await this._api.post("/patient/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePatients = async (data) => {
    try {
      const response = await this._api.post("/patient/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchPatients = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get("/patient/list", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CRM APIS

  //#region CLIENT

  static statusChange = async (itemId, data, path) => {
    const endpoint = `/client/${path}/status/`;
    try {
      const response = await this._api.post(`${endpoint}` + itemId, data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region Client Templates
  static addTemplate = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/templates",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateTemplate = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/templates/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeTemplate = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/templates/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleTemplates = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/templates/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTemplates = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/templates/list",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTemplatesListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/templates/list-page",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region TYPES
  static addType = async (data) => {
    try {
      const response = await this._api.post("/client/subclients/types", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateType = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeType = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleTypes = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/types/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTypes = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/subclients/types/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchTypesListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/types/list-page",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region ACCOUNTS
  static addAccount = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/accounts",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateAccount = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/accounts/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeAccount = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/subclients/accounts/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleAccounts = async (data) => {
    try {
      const response = await this._api.post(
        "/client/subclients/accounts/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAccounts = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/subclients/accounts/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchAccountsListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/subclients/accounts/list-page",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static exportAccountsCsv = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get(
        "/client/subclients/accounts/export-csv",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
          responseType: "blob",
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  
  static exportInterpretersCsv = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get(
        "/client/interpreters/export-csv",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          // params,
          responseType: "blob",
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static exportLanguagesCsv = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get(
        "/client/language/export-csv",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          // params,
          responseType: "blob",
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static importLanguages = async (formData) => {
    try {
      const response = await this._api.post("/client/language/import", formData, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static importLanguageTiers = async (formData) => {
    try {
      const response = await this._api.post("/client/language-tiers/import", formData, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static downloadLanguageImportDemoCsv = async () => {
    try {
      const response = await this._api.get("/client/language/import-demo", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        responseType: "blob",
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static downloadLanguageTierImportDemoCsv = async () => {
    try {
      const response = await this._api.get("/client/language-tiers/import-demo", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        responseType: "blob",
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static exportLanguageTiersCsv = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get(
        "/client/language-tiers/export-csv",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          // params,
          responseType: "blob",
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainClients = async (mainclient_id, query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        `/client/subclients/types/${mainclient_id}/dynamic-fields`,
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchSubClients = async (subclient_id, query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        `/client/subclients/accounts/${subclient_id}/dynamic-fields`,
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region  MainAccount access Subclinet

  static fetchMainSubAccountsList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/subaccounts/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static addMainSubAccount = async (data) => {
    try {
      const response = await this._api.post("/account/subaccounts", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateMainSubAccount = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/account/subaccounts/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMainSubAccount = async (itemId) => {
    try {
      const response = await this._api.post(
        "/account/subaccounts/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleMainSubAccounts = async (data) => {
    try {
      const response = await this._api.post(
        "/account/subaccounts/records",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static clientAccountId = async (id) => {
    try {
      const response = await this._api.get(
        `/account/subaccounts/${id}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountInvoices = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/invoices/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchClientMainAccountInvoice = async (id) => {
    try {
      const response = await this._api.get(`/account/invoices/${id}`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static reportInvoiceIssue = async (data) => {
    try {
      const response = await this._api.post('/account/invoices/issues', data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  

  static fetchInvoiceIssues = async (params = {}) => {
    try {
      const response = await this._api.get('/client/invoices/issues', {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateInvoiceIssue = async (data) => {
      try {
          const response = await this._api.post(
              "/client/invoices/invoice-issues/update",
              data,
              {
                  headers: {
                      Authorization: `Bearer ${Api._token}`,
                  },
              }
          );

          return response;
      } catch (error) {
          return error.response;
      }
  };

  //#endregion

  // Main account profile page slice
  // static mainAccountChangePassword = async (data) => {
  //   try {
  //     const response = await this._api.post("/changepassword", data, {
  //       headers: {
  //         "Authorization": `Bearer ${Api._token}`
  //       }
  //     });
  //     return response;
  //   } catch (error) {
  //     return error.response;
  //   }
  // };

  static fetchMainAccountProfile = async () => {
    try {
      const response = await this._api.get("/account/profile", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateMainAccountProfile = async (formData) => {
    try {
      const response = await this._api.post(
        "/account/profile/update",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region STAFF
  static addStaff = async (data) => {
    try {
      const response = await this._api.post("/client/staff", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateStaff = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/staff/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeStaff = async (itemId) => {
    try {
      const response = await this._api.post("/client/staff/delete/" + itemId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleStaff = async (data) => {
    try {
      const response = await this._api.post("/client/staff/records/", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchStaff = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/staff/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region DEPARTMENT
  static addDepartment = async (data) => {
    try {
      const response = await this._api.post("/client/tools/departments", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateDepartment = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/tools/departments/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeDepartment = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/tools/departments/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleDepartment = async (data) => {
    try {
      const response = await this._api.post(
        "/client/tools/departments/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchDepartments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/tools/departments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region FACILITIES
  static addFacility = async (data) => {
    try {
      const response = await this._api.post("/client/tools/facilities", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateFacility = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/tools/facilities/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeFacility = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/tools/facilities/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleFacilities = async (data) => {
    try {
      const response = await this._api.post(
        "/client/tools/facilities/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchFacilities = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/tools/facilities/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchFacilitiesByTypeId = async (id) => {
    try {
      const response = await this._api.get(`/client/subclients/types/facilities/${id}`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CLIENT Interpreter
  static addInterpreter = async (data) => {
    try {
      const response = await this._api.post("/client/interpreters", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateInterpreter = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/interpreters/edit/" + itemId,
        data,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeInterpreter = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/interpreters/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleInterpreters = async (data) => {
    try {
      const response = await this._api.post(
        "/client/interpreters/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreters = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/interpreters/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static generateCredentials = async () => {
    try {
      const response = await this._api.get("/client/interpreters/generate-credentials", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CLIENT Extensions
  static addExtensions = async (data) => {
    try {
      const response = await this._api.post("/client/extensions", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static addBulkExtensions = async (data) => {
    try {
      const response = await this._api.post("/client/extensions/bulk", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static updateExtensions = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/extensions/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeExtensions = async (itemId) => {
    try {
      const response = await this._api.post(
        `/client/extensions/delete/${itemId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleExtensions = async (data) => {
    data.ids = data?.ids?.map((value) => parseInt(value));

    try {
      const response = await this._api.post(
        "/client/extensions/records",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchExtensions = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/extensions/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchAssignees = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/extensions/assignees", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region CLIENT Appointment
  static addAppointment = async (data) => {
    try {
      const response = await this._api.post("/client/appointments", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        timeout: 90000,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static addRequest = async (data) => {
    try {
      const response = await this._api.post("/client/appointments/addAppointmentRequest", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        timeout: 90000,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateAppointment = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/appointments/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateRequest = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/appointments/edit-request/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static clientCancelRequest = async (itemId, reason = null) => {
    try {
      const data = {};
      if (reason) {
        data.reason = reason;
      }
      const response = await this._api.post(
        "/account/appointments/cancel-request/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static cancelRequest = async (itemId, reason = null) => {
    try {
      const data = {};
      if (reason) {
        data.reason = reason;
      }
      const response = await this._api.post(
        "/client/appointments/cancel-request/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static sendUpdateEmail = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/send-update-email/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static sendReassignEmail = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/send-reassign-email/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static sendAutoAssignmentEmail = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/send-auto-assignment-email/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeAppointment = async (itemId) => {
    console.log("Api._token", Api._token);
    try {
      const response = await this._api.post(
        `/client/appointments/delete/${itemId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleAppointments = async (data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/appointments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAppointmentRequests = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/dashboard/listAppointmentRequest", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchClientAppointmentRequests = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/dashboard/listAppointmentRequestByAccount", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static getAppointmentRequest = async (id) => {
    try {
      const response = await this._api.get(`/client/appointments/get-request/${id}`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static getClientAppointmentRequest = async (id) => {
    try {
      const response = await this._api.get(`/account/appointments/get-request/${id}`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAppointClients = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/invoices/appt-clients", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAppointClientsListPage = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/invoices/appt-clients-list",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchGeneratedInvoices = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/invoices/generated-invoices",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInvoiceHistory = async (invoiceId) => {
    try {
      const response = await this._api.get(
        `/client/invoices/history/${invoiceId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static deleteInvoiceHistory = async (id) => {
    try {
      const response = await this._api.post(
        "/client/invoices/delete-history/" + id,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static restoreInvoiceHistory = async (id) => {
    try {
      const response = await this._api.post(
        "/client/invoices/restore-history/" + id,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static makeHistoryCurrent = async (id) => {
    try {
      const response = await this._api.post(
        "/client/invoices/make-history-current/" + id,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static makeInvoice = async (data) => {
    try {
      const response = await this._api.post(
        "/client/invoices/make-invoice",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

    static makeMultipleInvoice = async (data) => {
    try {
      const response = await this._api.post(
        "/client/invoices/make-multiple-invoice",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static getLastInvoiceNumber = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/invoices/get-last-number",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static sendInvoiceEmail = async (data) => {
    try {
      const response = await this._api.post(
        "/client/invoices/send-invoice-email",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeInvoices = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/invoices/delete-invoices/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleInvoices = async (data) => {
    try {
      const response = await this._api.post(
        "/client/invoices/records-invoices",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static consolidateInvoices = async (invoiceIds, invoiceType) => {
    try {
      const response = await this._api.post(
        "/client/invoices/consolidate",
        { invoice_ids: invoiceIds, invoice_type: invoiceType },
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static downloadMultipleInvoices = async (invoiceIds, format = 'pdf') => {
    try {
      const response = await this._api.post(
        "/client/invoices/download-all",
        { invoice_ids: invoiceIds, attachment_format: format },
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          responseType: "blob",
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchCustomRate = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }

    try {
      const response = await this._api.get(
        "/client/subclients/types/get-rates",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region CLIENT DASHBOARD Appointment

  static fetchClientDashboardAppointment = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/dashboard/appointments", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchClientDashboardAnalytics = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/dashboard/analytics", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  // #region permission group
  static fetchAccessesList = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/permissions/groups/accesses",
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static addPermissionGroup = async (formData) => {
    try {
      const response = await this._api.post(
        "/client/permissions/groups",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updatePermissionGroup = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/permissions/groups/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removePermissionGroup = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/permissions/groups/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultiplePermissionGroup = async (data) => {
    try {
      const response = await this._api.post(
        "/client/permissions/groups/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchPermissionGroupList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/permissions/groups/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region Payments
  static handleMultipleStatus = async (data) => {
    try {
      const response = await this._api.post("/client/payments/status/", data, {

        headers: {
          "Authorization": `Bearer ${Api._token}`
        }
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAdminInterpreterPayments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/payments/list-interpreter", {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
        params
      }
      )
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAdminInterpreterPaymentsStatement = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/payments/paymentStatement", {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
        params
      }
      )
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static markInterpreterPaymentAsPaid = async (id) => {
    try {
      const response = await this._api.post(`/client/payments/markAsPaid`, {id:id}, {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        }
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAdminVendorPayments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/payments/list-vendor", {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
        params
      }
      )
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region STAFF
  static addSubUsers = async (data) => {
    try {
      const response = await this._api.post(
        "/client/permissions/sub-users",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateSubUsers = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/permissions/sub-users/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeSubUsers = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/permissions/sub-users/delete/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeMultipleSubUsers = async (data) => {
    try {
      const response = await this._api.post(
        "/client/permissions/sub-users/records/",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchSubUsers = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get(
        "/client/permissions/sub-users/list",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#region Main Account DASHBOARD Appointment

  static fetchMainAccountDashboardAppointment = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/dashboard/appointments", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountDashboardAnalytics = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/dashboard/analytics", {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/appointments/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountAppointmentView = async (itemId) => {
    try {
      const response = await this._api.get(
        "/account/appointments/get/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchMainAccountAppointmentLogs = async (id, data, endpoint) => {
    try {
      const response = await this._api.get(
        `/account/appointments/get-logs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region CLIENT APPOINTMENT VIEW
  static fetchClientAppointmentView = async (itemId) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get/" + itemId,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region APPOINTMENTS ACTIONS BUTTONS APIS
  static setActionAssign = async (id, data, endpoint) => {
    try {
      const response = await this._api.post(
        `/client/appointments/actions/${endpoint}/${id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          timeout: 90000,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  static assignInterpreter = async (data, endpoint) => {
    try {
      const response = await this._api.post(
        `/client/appointments/actions/${endpoint}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          timeout: 90000,
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchAdminNotificationsUnread = async () => {
    try {
      const response = await this._api.get("/client/admin/notifications/unread", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static acknowledgeAdminNotification = async (notificationId) => {
    try {
      const response = await this._api.post(
        `/client/admin/notifications/${notificationId}/acknowledge`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static cancelAppointmentAndNotifyClient = async (appointmentId, reason = null) => {
    try {
      const data = {};
      if (reason) {
        data.reason = reason;
      }
      const response = await this._api.post(
        `/client/appointments/actions/notification-cancel/${appointmentId}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region GET APPOINTMENTS LOGS
  static fetchAppointmentLogs = async (id, data, endpoint) => {
    try {
      const response = await this._api.get(
        `/client/appointments/get-logs/${id}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static feedbackVerfication = async (token) => {
    try {
      const response = await this._api.get(
        `/feedback/verify-token?token=${token}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static submitFeedback = async (data) => {
    try {
      const response = await this._api.post(
        `/feedback/submit`,
        data
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  // //#region APPOINTMENTS ACTIONS BUTTONS APIS
  // static inviteInterpretersActions = async (id, data, endpoint) => {
  //   try {
  //     const response = await this._api.post(`/client/appointments/actions/${endpoint}/${id}`, data, {
  //       headers: {
  //         "Authorization": `Bearer ${Api._token}`
  //       },
  //     });
  //     return response;
  //   } catch (error) {
  //     return error.response;
  //   }
  // };
  // //#endregion

  //#region CLIENT Appointments invite interpreters

  static fetchAppointmentsInterpreters = async (id) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get-interpreters/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchInterpretersWithAppointments = async (id) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get-interpreters-with-appointments/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };


  static fetchInterpreterRates = async (appointment_id, interpreter_id) => {
    const params = {
      appointment_id,
      interpreter_id
    }
    try {
      const response = await this._api.get(
        "/client/appointments/get-interpreter-rates",
        {
          params,
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static submitApptCustomWorkOrder = async (data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/set-interpreter-rates",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchInvitedInterpreters = async (id) => {
    try {
      const response = await this._api.get(
        "/client/appointments/get-invited-interpreters/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static inviteInterpreters = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/invite-interpreters/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static removeInvitedInterpreters = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/appointments/clear-invites/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region CLIENT Appointments INVOICES

  static fetchVerifiedInvoices = async (page, query) => {
    let params = { page: page, verified: 1 };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/invoices/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchUnVerifiedInvoices = async (page, query) => {
    let params = { page: page, verified: 0 };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/invoices/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateInvoice = async (data, itemId) => {
    try {
      const response = await this._api.post(
        "/client/invoices/edit/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static verifyInvoice = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/invoices/verify/" + itemId,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion

  //#region GET APPOINTMENTS invite
  static fetchInterviewInvites = async (token) => {
    try {
      const response = await this._api.get("/get-appointment/" + token, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static acceptInterviewInvites = async (token) => {
    try {
      const response = await this._api.post("/accept-appointment/" + token, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static rejectInterviewInvites = async (token) => {
    try {
      const response = await this._api.post("/reject-appointment/" + token, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#endregion


  //#region Interpreter access api


  static fetchInterpreterDashboardAppointment = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/dashboard/appointments", {
        params,
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
      }
      )
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterDashboardAnalytics = async (query) => {
    let params = {};
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/dashboard/analytics", {
        params,
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
      }
      )
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterAppointments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/appointments/list", {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
        params
      }
      )
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterAppointmentView = async (itemId) => {
    try {
      const response = await this._api.get("/interpreter/appointments/get/" + itemId, {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterAppointmentLogs = async (id, data, endpoint) => {
    try {
      const response = await this._api.get(`/interpreter/appointments/get-logs/${id}`, {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterProfile = async () => {
    try {
      const response = await this._api.get("/interpreter/profile", {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
      }
      )
      return response;
    } catch (error) {
      return error.response;
    }
  };


  static updateInterpreterProfile = async (formData) => {
    try {
      const response = await this._api.post("/client/interpreter/profile/update", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${Api._token}`
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateVendorAgreement = async (interpreterId, formData) => {
    try {
      const response = await this._api.post(`/client/interpreters/${interpreterId}/vendor-agreement`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${Api._token}`
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static addInterpreterAttachment = async (interpreterId, formData) => {
    try {
      const response = await this._api.post(`/client/interpreters/${interpreterId}/attachments`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${Api._token}`
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static updateInterpreterAttachment = async (attachmentId, formData) => {
    try {
      const response = await this._api.post(`/client/interpreters/attachments/${attachmentId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${Api._token}`
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static deleteInterpreterAttachment = async (attachmentId) => {
    try {
      const response = await this._api.delete(`/client/interpreters/attachments/${attachmentId}`, {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static getInterpreterAttachments = async (interpreterId) => {
    try {
      const response = await this._api.get(`/client/interpreters/${interpreterId}/attachments`, {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterPayments = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/payments/list", {
        headers: {
          "Authorization": `Bearer ${Api._token}`
        },
        params
      }
      )
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  //#endregion




  // #region Client Translation
  static submitClientTranslationForm = async (formData) => {
    try {
      const response = await this._api.post("/account/translations", formData, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchclientTranslationList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/account/translations/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchclientTranslationById = async (transId) => {
    try {
      const response = await this._api.get(
        `/account/translations/get/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

   static fetchclientTransLogs = async (transId) => {
    try {
      const response = await this._api.get(
        `/account/translations/get-logs/${transId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

   static approveClientQuote = async (transId) => {
    try {
      const response = await this._api.post(
        `/account/quotations/approve/${transId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static rejectClientQuote = async (transId, payload) => {
    try {
      const response = await this._api.post(
        `/account/quotations/reject/${transId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static clientCancelTranslation = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/account/translations/actions/cancel/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };



  //#region Interpreter translation
  static fetchInterpreterTranslationList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/interpreter/translations/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

// Interpreter translation By Id
  static fetchinterpreterTranslationById = async (transId) => {
    try {
      const response = await this._api.get(
        `/interpreter/translations/get/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

    // Accept translation
   static acceptTranslationById = async (transId) => {
    try {
      const response = await this._api.post(
        `/interpreter/translations/actions/accept-invite/${transId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

   // Decline translation
  static declineTranslationById = async (transId, payload) => {
    try {
      const response = await this._api.post(
        `/interpreter/translations/actions/decline-invite/${transId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

   // Interpreter Decline Translation
  static interpreterDeclineTranslation = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/interpreter/translations/actions/decline-translation/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

   // Interpreter Upload Translation Document
  static interpreterTranslationUploadDocument = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/interpreter/translations/actions/upload-translated-files/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // Get Translator Rates
  static getTranslatorRates = async (data) => {
    try {
      const response = await this._api.post(
        "/client/translations/get-translator-rates",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return error.response?.data || error;
    }
  };

  // Set Translator Rates (Custom Work Order)
  static setTranslatorRates = async (data) => {
    try {
      const response = await this._api.post(
        "/client/translations/set-translator-rates",
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return error.response?.data || error;
    }
  };

   // #region admin Translation
    static fetchTranslation = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/translations/list", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
    };

    static fetchClientTranslation = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/translations/single-client", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
    };

      static fetchTranslationById = async (transId) => {
      try {
        const response = await this._api.get(
          `/client/translations/get/${transId}`,
          {
            headers: {
              Authorization: `Bearer ${Api._token}`,
            },
          }
        );
        return response;
      } catch (error) {
        return error.response;
      }
    };

    static getTranslators = async (transId) => {
    try {
      const response = await this._api.get(
        `/client/translations/get-translators/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
    };


    static fetchTranslator = async () => {
      try {
        const response = await this._api.get(
          "/client/translations/get-all-translators",
          {
            headers: {
              Authorization: `Bearer ${Api._token}`,
            },
          }
        );
        return response;
      } catch (error) {
        return error.response;
      }
    };


      static getInvitedTranslators = async (transId) => {
      try {
        const response = await this._api.get(
          `/client/translations/get-invited-translators/${transId}`,
          {
            headers: {
              Authorization: `Bearer ${Api._token}`,
            },
          }
        );
        return response;
      } catch (error) {
        return error.response;
      }
    };


     static inviteTranslators = async (itemId, data) => {
      try {
        const response = await this._api.post(
          "/client/translations/invite-translators/" + itemId,
          data,
          {
            headers: {
              Authorization: `Bearer ${Api._token}`,
            },
          }
        );
        return response;
      } catch (error) {
        return error.response;
      }
    };
    
    static removeInvitedTranslators = async (itemId, data) => {
      try {
        const response = await this._api.post(
          "/client/translations/actions/remove-invited-translators/" + itemId,
          data,
          {
            headers: {
              Authorization: `Bearer ${Api._token}`,
            },
          }
        );
        return response;
      } catch (error) {
        return error.response;
      }
    };

     static fetchTransLogs = async (transId) => {
      try {
        const response = await this._api.get(
          `/client/translations/get-logs/${transId}`,
          {
            headers: {
              Authorization: `Bearer ${Api._token}`,
            },
          }
        );
        return response;
      } catch (error) {
        return error.response;
      }
      };


    // Update Revise Quotes
  static updateReviseQuote = async (transId, payload) => {
    try {
      const response = await this._api.post(
        `/client/quotations/update/${transId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // Revise Quote
  static fetchReviseQuote = async (transId) => {
    try {
      const response = await this._api.get(
        `/client/quotations/latest/${transId}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

   static approveQuote = async (transId) => {
    try {
      const response = await this._api.post(
        `/client/quotations/approve/${transId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

    static rejectQuote = async (transId, payload) => {
    try {
      const response = await this._api.post(
        `/client/quotations/reject/${transId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

    static assignTranslators = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/assign-translator/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static reassignTranslators = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/reassign-translator/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

   static translationApproveSubmission = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/approve-submission/" + itemId,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static markTranslationCompleted = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/mark-completed/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

   static translationRejectSubmission = async (itemId, payload) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/reject-submission/" + itemId,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

static removeTranslation = async (itemId) => {
  console.log("itemIdddddd", itemId);
  try {
    const response = await this._api.post(
      `/client/translations/delete/${itemId}`,  // ID in URL
      {}, // body can be empty
      {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      }
    );
    return response;
  } catch (error) {
    return error.response;
  }
};

  static translationMarkInvoiceAsPaid = async (itemId) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/change-invoice-status/" + itemId,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

    static translationCancel = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/cancel/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

    static translationUploadDocument = async (itemId, data) => {
    try {
      const response = await this._api.post(
        "/client/translations/actions/upload-translated-files/" + itemId,
        data,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };


   static fetchClient = async (params = {}) => {
  try {
    const response = await this._api.get("/client/subclients/types/list", {
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
      params,
    });
    return response;
  } catch (error) {
    return error.response;
  }
};


static submitTranslation = async (formData) => {
  try {
    const response = await this._api.post("/client/translations", formData, {
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    console.log(error, "errorrrr");
    throw error; 
  }
};

static updateTranslation = async (formData, itemId) => {
  try {
    const response = await this._api.post(
      `/client/translations/edit/${itemId}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.log(error, "errorrrr");
    throw error;
  }
};

  // admin Join Video Call
  static adminjoinVideoCall = async (appointmentId) => {
    try {
      const response = await Api._api.post(
        `/client/appointments/actions/join-room/${appointmentId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

//  interpriters Join Video Call
    static interpreterjoinVideoCall = async (appointmentId) => {
    try {
      const response = await Api._api.post(
        `/interpreter/appointments/actions/join-room/${appointmentId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static interpreterJoinExternalVideoCall = async (appointmentId) => {
    try {
      const response = await Api._api.post(
        `/interpreter/appointments/actions/join-external-room/${appointmentId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };



  // requester Join Video Call
    static requesterJoinRoom = async (token) => {
    try {
      const response = await Api._api.post(`/join-room/${token}`, {});
      return response;
    } catch (error) {
      return error.response;
    }
  };


    //#region appoinment details
  static getAppointmentDetail = async (token) => {
    try {
      const response = await Api._api.get(
        `/get-appointment-requester/${token}`,
        {}
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

    //#region VRI CALLING
  static acceptVriCall = async (id) => {
    try {
      const response = await this._api.post(
        `/interpreter/appointments/actions/answer-vri-call/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // invite Guest 
   static inviteGuests = async (appointmentId, payload) => {
    try {
      const response = await this._api.post(
        `/client/appointments/actions/invite-guests/${appointmentId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static toggleCheckIn = async () => {
  try {
    const response = await this._api.post(
      `/client/toggle-checkin`,
      {},
      {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      }
    );
    return response;
  } catch (error) {
    return error.response;
  }
};

  static fetchAdminInterpreterTimesheets = async (page = 1, params = {}) => {
    try {
      const response = await this._api.get(`/client/timesheets/get?page=${page}`, {
        params, // includes from_date, to_date, interpreter_id etc.
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchStaffTimesheets = async (page = 1, params = {}) => {
  try {
    const response = await this._api.get(`/client/timesheets/staff/get?page=${page}`, {
      params, // includes from_date, to_date, staff_id etc.
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};
  static fetchSingleStaffTimesheets = async (page = 1, params = {}) => {
  try {
    const response = await this._api.get(`/client/timesheets/staff/get-single?page=${page}`, {
      params, // includes from_date, to_date, staff_id etc.
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

  static fetchInterpreterTimesheets = async (page = 1, params = {}) => {
  try {
    const response = await this._api.get(`/client/timesheets/interpreter/get?page=${page}`, {
      params,
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};
  static fetchClientTimesheets = async (page = 1, params = {}) => {
  try {
    const response = await this._api.get(`/client/timesheets/client/get?page=${page}`, {
      params,
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

static fetchStaffs = async (page = 1, params = {}) => {
  try {
    const response = await this._api.get(`/client/timesheets/get-staff?page=${page}`, {
      params,
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};


static fetchInterpreter = async (page = 1, params = {}) => {
  try {
    const response = await this._api.get(`/client/timesheets/get-interpreter?page=${page}`, {
      params,
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};
static getCheckInStatus = async () => {
  try {
    const response = await this._api.get(`/client/checkin-status`, {
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

static fetchExternalInterpreters = async (page = 1, params = {}) => {
  try {
    const response = await this._api.get(`/client/interpreters/external?page=${page}`, {
      params,
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

static getInterpreterById = async (id) => {
  try {
    const response = await this._api.get(`/client/interpreters/${id}`, {
      headers: {
        Authorization: `Bearer ${Api._token}`,
      },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

static fetchSingleType = async (id) => {
  try {
    const response = await this._api.get(`/client/subclients/types/get-single/${id}`,
      {
        headers: { Authorization: `Bearer ${Api._token}` },
      }
    );
    return response;
  } catch (error) {
    return error.response;
  }
};


static fetchTranslationPricing = async (clientId, targetLanguageIds) => {
  try {
    const response = await this._api.get(
      `/client/subclients/types/translation-pricing/${clientId}`,
      {
        params: { target_languages: targetLanguageIds },
        headers: { Authorization: `Bearer ${Api._token}` },
      }
    );
    return response;
  } catch (error) {
    return error.response;
  }
};

// static fetchSingleTypeSubject = async (id) => {
//   try {
//     const response = await this._api.get(`/client/subclients/types/get-single-subject/${id}`,
//       {
//         headers: { Authorization: `Bearer ${Api._token}` },
//       }
//     );
//     return response;
//   } catch (error) {
//     return error.response;
//   }
// };

static fetchSingleTypeSubject = async (id) => {
  try {
    const response = await this._api.get(`/client/subclients/types/get-single-subject/${id}`, {
      headers: { Authorization: `Bearer ${Api._token}` },
    });
    return response;
  } catch (error) {
    return error.response;
  }
};

static fetchSingleTemplate = async (id) => {
  try {
    const response = await this._api.get(
      `/client/subclients/templates/get-single/${id}`,
      {
        headers: { Authorization: `Bearer ${Api._token}` },
      }
    );
    return response;
  } catch (error) {
    return error.response;
  }
};

static fetchSinglePermission = async (id) => {
    try {
        const response = await this._api.get(`/client/permissions/groups/get-single/${id}`, {
            headers: { Authorization: `Bearer ${Api._token}` },
        });
        return response;
    } catch (error) {
        return error.response;
    }
  };
  
  static answerAppointmentInvite = async (apptId, payload) => {
    try {
      const response = await this._api.post(
        `/interpreter/appointments/actions/answer-invite/${apptId}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };


  static Confirmation = async (token) => {
    try {
      const response = await this._api.get(
        `confirmation/${token}`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static cancelConfirmation = async (token, payload) => {
    try {
      const response = await this._api.post(
        `confirmation/${token}/cancel`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static confirmConfirmation = async (token) => {
    try {
      const response = await this._api.post(
        `confirmation/${token}/confirm`,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
        }
      );
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static downloadInterpreterPaymentsStatementCsv = async (id) => {
    
    try {
      const response = await this._api.get(
        "/client/payments/downloadPaymentStatement/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          responseType: "blob",
        }
      );

      return response;
    } catch (error) {
      return error.response;
    }
  };
  static downloadInterpreterPaymentsStatementAttachedFile = async (id) => {
    
    try {
      const response = await this._api.get(
        "/client/payments/downloadPaymentStatement/attached-file/" + id,
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          responseType: "blob",
        }
      );

      return response;
    } catch (error) {
      return error.response;
    }
  };
  // static downloadInterpreterPaymentsCsv = async (query) => {
  //   let params = {};

  //   if (query && Object.keys(query).length > 0) {
  //     Object.keys(query).map((key) => {
  //       if (query[key]) {
  //         params[key] = query[key];
  //       }
  //     });
  //   }

  //   try {
  //     const response = await this._api.get(
  //       "/client/payments/export-csv",
  //       {
  //         headers: {
  //           Authorization: `Bearer ${Api._token}`,
  //         },
  //         params,
  //         responseType: "blob",
  //       }
  //     );

  //     return response;
  //   } catch (error) {
  //     return error.response;
  //   }
  // };

  static downloadInterpreterPaymentsCsv = async (query) => {
    let params = {};

    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((key) => {
        if (query[key]) {
          params[key] = query[key];
        }
      });
    }

      const response = await this._api.get(
        "/client/payments/export-csv",
        {
          headers: {
            Authorization: `Bearer ${Api._token}`,
          },
          params,
          responseType: "blob",
        }
      );

      return response;
   };

   static connectQuickBooks = async () => {
    try {
      const response = await this._api.get("/quickbooks/connect", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  
  static fetchSingleAdminInterpreterTimesheets = async (params = {}) => {
    try {
      const response = await this._api.get(`/client/timesheets/get-single-details`, {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  
  static fetchInterpretersList = async (page = 1, params = {}) => {
    try {
      const response = await this._api.get(`/client/interpreters/filtered-list?page=${page}`, {
        params,
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  static fetchAppointmentInterpretersList = async (page, query) => {
    let params = { page: page };
    if (query && Object.keys(query).length > 0) {
      Object.keys(query).map((value) => {
        if (query[value]) {
          params[value] = query[value];
        }
      });
    }
    try {
      const response = await this._api.get("/client/interpreters/listInterpreter", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
        params,
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  //#region CHAT APIS
  static fetchChatMessages = async (receiverId) => {
    try {
      const response = await this._api.get("/client/chat/messages/" + receiverId, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static sendChatMessage = async (data) => {
    try {
      const response = await this._api.post("/client/chat/send", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  //#endregion

  static fetchChatInterpreters = async () => {
    try {
      const response = await this._api.get(`client/chat/interpreters`, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };
  
  static addInterpreterChat = async (id) => {
    try {
      const response = await this._api.post("/client/chat/add-interpreters/" + id, {}, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static fetchInterpreterChatMessages = async () => {
    try {
      const response = await this._api.get("/interpreter/chat/messages/", {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static sendInterpreterChatMessage = async (data) => {
    try {
      const response = await this._api.post("/interpreter/chat/send", data, {
        headers: {
          Authorization: `Bearer ${Api._token}`,
        },
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static makeMultipleInvoiceBatch = async (data) => {
      try {
          const response = await this._api.post(
              "/client/invoices/make-multiple-invoice-batch",
              data,
              {
                  headers: {
                      Authorization: `Bearer ${Api._token}`,
                  },
              }
          );
          return response;
      } catch (error) {
          return error.response;
      }
  };

  static updateInvoice = async (data, itemId) => {
      try {
          const response = await this._api.post(
              `/client/invoices/editInvoice/${itemId}`,
              data,
              {
                  headers: {
                      Authorization: `Bearer ${Api._token}`,
                  },
              }
          );
          return response;
      } catch (error) {
          return error.response;
      }
  };

  static fetchClientRates = async (params = {}) => {
      try {
          const response = await this._api.get(`/client/subclients/types/get-rates`, {
              params,
              headers: {
                  Authorization: `Bearer ${Api._token}`,
              },
          });
          return response;
      } catch (error) {
          return error.response;
      }
  };

  
  static createInterpreterPaymentStatement = async (formData) => {
    try {
      const response = await this._api.post("/client/payments/export-attached-csv", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "Authorization": `Bearer ${Api._token}`
        },
        responseType: "blob",
      });
      return response;
    } catch (error) {
      return error.response;
    }
  };

  // ─── Public Invoice Issue (no auth required) ──────────────────────────────

  static getPublicInvoiceIssueByToken = async (token) => {
    try {
      const response = await this._api.get(`/public/invoice-issue/${token}`);
      return response;
    } catch (error) {
      return error.response;
    }
  };

  static submitPublicInvoiceIssue = async (token, formData) => {
    try {
      const response = await this._api.post(`/public/invoice-issue/${token}`, formData);
      return response;
    } catch (error) {
      return error.response;
    }
  };
}
